import React from 'react';
import { useMessages } from '../../context/MessagesContext';
import { useUISettings } from '../../context/UISettingsContext';
import { FileMessageBubble } from './FileMessageBubble';

export function MessageBubble({ message, isOwn, contact }) {
  const { deleteMessage } = useMessages();
  const { setIsPrivateKeyModalOpen } = useUISettings();

  const handleDecryptClick = () => {
    setIsPrivateKeyModalOpen(true);
  };

  const handleDeleteClick = () => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      deleteMessage(message.id);
    }
  };

  const getFormattedTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getReadStatusIcon = () => {
    if (message.readAt) {
      return (
        <span title="Read" className="text-blue-500">
          ✓✓
        </span>
      );
    }
    if (message.deliveredAt) {
      return (
        <span title="Delivered" className="text-gray-500">
          ✓✓
        </span>
      );
    }
    return (
      <span title="Sent" className="text-gray-400">
        ✓
      </span>
    );
  };

  // Check if this is a file message
  const isFileMessage = message.hasAttachment && message.attachment;

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} message-item`}>
      {!isOwn && (
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2 flex-shrink-0">
          <span className="text-blue-600 font-bold text-sm">
            {(contact?.alias || contact?.contactUuid || '?')[0].toUpperCase()}
          </span>
        </div>
      )}
      
      <div className={`relative group max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
        {isFileMessage ? (
          <FileMessageBubble fileInfo={message.attachment} isOwn={isOwn} />
        ) : (
          <div 
            className={`p-3 rounded-2xl break-words shadow ${
              isOwn 
                ? 'bg-blue-500 text-white rounded-br-none' 
                : message.decrypted 
                  ? 'bg-white text-gray-800 rounded-bl-none' 
                  : 'bg-gray-200 text-gray-600 rounded-bl-none'
            } ${message.expiresAt ? 'border-2 border-yellow-400' : ''}`}
          >
            {message.decrypted ? (
              <p className="text-sm whitespace-pre-wrap leading-relaxed">
                {message.decrypted}
              </p>
            ) : (
              <div className="flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm italic">Encrypted message</span>
                {!isOwn && (
                  <button
                    onClick={handleDecryptClick}
                    className="text-xs bg-teal-500 hover:bg-teal-600 text-white px-2 py-1 rounded transition"
                  >
                    Decrypt
                  </button>
                )}
              </div>
            )}
            
            <div className={`flex items-center justify-end mt-1 space-x-2 text-xs ${
              isOwn ? 'text-blue-200' : 'text-gray-400'
            }`}>
              <span>{getFormattedTime(new Date(message.timestamp))}</span>
              
              {message.isSelfDestruct && (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              )}
              
              {isOwn && (
                <span className="ml-1">
                  {getReadStatusIcon()}
                </span>
              )}
            </div>
            
            {message.expiresAt && (
              <div className="absolute -top-2 -right-2 bg-yellow-400 text-white text-xs px-2 py-1 rounded-full">
                ⏱️ {Math.ceil((new Date(message.expiresAt) - new Date()) / 1000 / 60)}m
              </div>
            )}
          </div>
        )}
        
        {isOwn && (
          <button
            onClick={handleDeleteClick}
            className="absolute -right-8 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
            title="Delete message"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
      
      {isOwn && <div className="w-10"></div>}
    </div>
  );
}